import { Component, OnInit, OnDestroy, signal, inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { RouterLink } from '@angular/router';
import { APP_CONFIG } from '../../config/app.config.constants';
import { TrackingService } from '../../services/tracking.service';

@Component({
  selector: 'app-lead-popup',
  standalone: true,
  imports: [CommonModule, RouterLink],
  template: `
    @if (isVisible()) {
      <div class="popup-overlay" (click)="onOverlayClick($event)">
        <div class="popup-container" [class.popup-enter]="isAnimating()">
          <!-- Close Button -->
          <button class="popup-close" (click)="closePopup()" aria-label="Close popup">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>

          <!-- Creative Image (clickable - navigates to demo) -->
          <a routerLink="/demo" class="popup-image-wrapper" (click)="onImageClick()">
            <img
              [src]="popupImage"
              alt="Kalaivani ERP - Smart School Management Software"
              class="popup-image"
              loading="eager"
            />
          </a>

          <!-- CTA Buttons -->
          <div class="popup-actions">
            <a routerLink="/demo" class="btn btn-primary popup-btn" (click)="onDemoClick()">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polygon points="5 3 19 12 5 21 5 3"></polygon>
              </svg>
              Book FREE Demo
            </a>
            <a [href]="whatsappUrl" target="_blank" rel="noopener noreferrer" class="btn popup-btn popup-btn-whatsapp" (click)="onWhatsAppClick()">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              WhatsApp Us
            </a>
          </div>
        </div>
      </div>
    }
  `,
  styles: [`
    .popup-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.6);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
      padding: 20px;
      backdrop-filter: blur(4px);
      animation: overlayFadeIn 0.3s ease;
    }

    @keyframes overlayFadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    .popup-container {
      background: white;
      border-radius: 16px;
      max-width: 520px;
      width: 100%;
      position: relative;
      overflow: hidden;
      box-shadow: 0 25px 60px rgba(0, 0, 0, 0.3);
      transform: scale(0.9) translateY(20px);
      opacity: 0;
      transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
    }

    .popup-container.popup-enter {
      transform: scale(1) translateY(0);
      opacity: 1;
    }

    .popup-close {
      position: absolute;
      top: 10px;
      right: 10px;
      background: rgba(255, 255, 255, 0.9);
      border: none;
      cursor: pointer;
      padding: 6px;
      border-radius: 50%;
      color: var(--text-primary);
      transition: all 0.2s ease;
      z-index: 2;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }

    .popup-close:hover {
      background: white;
      transform: rotate(90deg) scale(1.1);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    }

    .popup-image-wrapper {
      display: block;
      width: 100%;
      line-height: 0;
      cursor: pointer;
      transition: opacity 0.2s ease;
    }

    .popup-image-wrapper:hover {
      opacity: 0.92;
    }

    .popup-image {
      width: 100%;
      height: auto;
      display: block;
      object-fit: cover;
    }

    .popup-actions {
      display: flex;
      gap: 10px;
      padding: 16px 20px;
    }

    .popup-btn {
      flex: 1;
      padding: 12px 16px;
      font-size: 0.9rem;
      font-weight: 600;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
    }

    .popup-btn-whatsapp {
      background: #25D366;
      color: white;
      border: none;
      box-shadow: 0 4px 14px rgba(37, 211, 102, 0.3);
    }

    .popup-btn-whatsapp:hover {
      background: #1EBE5A;
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(37, 211, 102, 0.4);
    }

    @media (min-width: 1024px) {
      .popup-container {
        max-width: 580px;
        border-radius: 20px;
      }

      .popup-close {
        top: 14px;
        right: 14px;
        padding: 8px;
      }

      .popup-actions {
        gap: 14px;
        padding: 20px 24px;
      }

      .popup-btn {
        padding: 14px 20px;
        font-size: 1rem;
        border-radius: 12px;
      }
    }

    @media (min-width: 1440px) {
      .popup-container {
        max-width: 640px;
      }

      .popup-actions {
        gap: 16px;
        padding: 24px 28px;
      }

      .popup-btn {
        padding: 16px 24px;
        font-size: 1.05rem;
      }
    }

    @media (max-width: 480px) {
      .popup-overlay {
        padding: 12px;
        align-items: flex-end;
      }

      .popup-container {
        border-radius: 16px 16px 10px 10px;
        max-height: 90vh;
        overflow-y: auto;
      }

      .popup-actions {
        flex-direction: column;
        gap: 8px;
        padding: 12px 16px;
      }

      .popup-btn {
        padding: 12px 16px;
        font-size: 0.875rem;
      }
    }
  `]
})
export class LeadPopupComponent implements OnInit, OnDestroy {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly trackingService = inject(TrackingService);

  isVisible = signal(false);
  isAnimating = signal(false);

  private popupTimer: any;
  private readonly POPUP_DELAY_MS = 5000;
  private readonly STORAGE_KEY = 'kalaivani_lead_popup_dismissed';
  private readonly DISMISS_HOURS = 24;

  whatsappUrl = '';
  popupImage = '/images/lead-popup-creative.jpg';

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return;

    const message = encodeURIComponent(
      'Hi, I\'m interested in Kalaivani ERP for my school. Could you share more details?'
    );
    this.whatsappUrl = `https://wa.me/${APP_CONFIG.WHATSAPP_PHONE}?text=${message}`;

    if (this.shouldShowPopup()) {
      this.popupTimer = setTimeout(() => {
        this.showPopup();
      }, this.POPUP_DELAY_MS);
    }
  }

  ngOnDestroy() {
    if (this.popupTimer) {
      clearTimeout(this.popupTimer);
    }
  }

  showPopup() {
    this.isVisible.set(true);
    setTimeout(() => this.isAnimating.set(true), 10);
  }

  closePopup() {
    this.isAnimating.set(false);
    setTimeout(() => {
      this.isVisible.set(false);
      this.markDismissed();
    }, 300);
  }

  onOverlayClick(event: Event) {
    if ((event.target as HTMLElement).classList.contains('popup-overlay')) {
      this.closePopup();
    }
  }

  private shouldShowPopup(): boolean {
    try {
      const dismissed = localStorage.getItem(this.STORAGE_KEY);
      if (!dismissed) return true;

      const dismissedAt = parseInt(dismissed, 10);
      const hoursElapsed = (Date.now() - dismissedAt) / (1000 * 60 * 60);
      return hoursElapsed >= this.DISMISS_HOURS;
    } catch {
      return true;
    }
  }

  private markDismissed() {
    try {
      localStorage.setItem(this.STORAGE_KEY, Date.now().toString());
    } catch {
      // localStorage unavailable
    }
  }

  onImageClick(): void {
    this.trackingService.trackDemoCTAClick('lead_popup_image');
    this.closePopup();
  }

  onDemoClick(): void {
    this.trackingService.trackDemoCTAClick('lead_popup_demo');
    this.closePopup();
  }

  onWhatsAppClick(): void {
    this.trackingService.trackWhatsAppClick('lead_popup_whatsapp');
    this.closePopup();
  }
}
